import {format, parseISO} from 'date-fns';
import slugify from 'slugify';
import {markRaw} from 'vue';
import type {PageTypeFormStoryblok} from '../storyblok/types';
import RichText from '@/components/RichText.vue';
import {NuxtIcon} from '#components';

const classes = {
	input: 'max-w-none !py-2.5',
	legend: 'form-label mx-0',
};

const RawRichText = markRaw(RichText);
const RawNuxtIcon = markRaw(NuxtIcon);

function generateLabelRequired(label: string, required: boolean) {
	return {
		label: {
			children: [
				label,
				{
					$el: required ? 'span' : null,
					children: required ? '*' : '',
					attrs: {class: required ? 'ml-1 text-body-important' : ''},
				},
			],
		},
	};
}

export function getFormSchema(fields: PageTypeFormStoryblok['groupContentFields']) {
	const {isMobileOrTablet} = useDevice();

	return fields?.map((field) => {
		switch (field.component) {
			case 'formInput':
				return {
					$formkit: field.input_type,
					name: `content.${field._uid}.text`,
					label: field.label,
					placeholder: field.hint,
					validation: field.required ? 'required' : undefined,
					classes,
					sectionsSchema: {...generateLabelRequired(field.label, !!field.required)},
				};
			case 'formSelectbox':
				return {
					$formkit: 'select',
					name: `content.${field._uid}.text`,
					label: field.label,
					placeholder: field.hint,
					validation: field.required ? 'required' : undefined,
					classes,
					options: field.options?.map((option) => option.label) || [],
					sectionsSchema: {...generateLabelRequired(field.label, !!field.required)},
				};
			case 'formNote':
				return {
					$formkit: 'textarea',
					name: `content.${field._uid}.text`,
					label: field.label,
					placeholder: field.hint,
					validation: field.required ? 'required' : undefined,
					classes,
					sectionsSchema: {...generateLabelRequired(field.label, !!field.required)},
				};
			case 'formDate':
				return {
					$formkit: isMobileOrTablet ? 'date' : 'datepicker',
					name: `content.${field._uid}.text`,
					label: field.label,
					validation: field.required ? 'required' : undefined,
					min: field.dateFrom ? format(parseISO(field.dateFrom), 'yyyy-MM-dd') : undefined,
					max: field.dateTo ? format(parseISO(field.dateTo), 'yyyy-MM-dd') : undefined,
					placeholder: 'DD. MM. RRRRR',
					classes: {
						...classes,
						label: 'form-label mx-0',
					},
					sectionsSchema: {...generateLabelRequired(field.label, !!field.required)},
				};
			case 'formParagraph':
				return {
					$el: 'div',
					attrs: {class: 'formkit-paragraph'},
					children: [
						{
							$el: 'strong',
							attrs: {class: 'mb-3 block text-sm'},
							children: field.label,
						},
						{
							$cmp: RawRichText,
							props: {
								data: field.text,
								class: 'text-sm',
							},
						},
					],
				};
			case 'formCheckbox':
				return {
					$formkit: field.multiple ? 'checkbox' : 'radio',
					name: `content.${field._uid}.${field.multiple ? 'list' : 'text'}`,
					label: field.visibleLabel ? slugify(field.label, {lower: true}) : undefined,
					validation: field.required ? 'required' : undefined,
					classes,
					options: field.options?.map((option) => ({
						value: option.shortLabel,
						label: option.label,
					})) || [],
					sectionsSchema: {
						legend: {
							children: [
								field.label,
								{
									$el: field.required ? 'span' : null,
									children: field.required ? '*' : '',
									attrs: {class: field.required ? 'ml-1 text-body-important' : ''},
								},
							],
						},
						label: {
							children: [
								{
									$cmp: RawRichText,
									props: {data: '$option.label'},
								},
							],
						},
						decorator: {
							children: [
								{
									$cmp: RawNuxtIcon,
									props: field.multiple ? {name: 'check', filled: true, class: 'flex h-full w-full items-center justify-center text-xl'} : {name: 'circle', class: 'flex h-full w-full items-center justify-center typo-body-caption'},
								},
							],
						},
					},
				};
			default:
				return null;
		}
	}).filter(Boolean);
}
